import React, { useMemo, useState } from "react"
import { cn } from "~/common/cn"
import { Button } from "~/ui/button"
import { Tag } from "~/ui/tag"
import { Popover, PopoverContent, PopoverTrigger } from "~/ui/popover"

interface Tag {
  id: string
  name: string
}

interface TagFilterProps {
  tags: Tag[]
  selectedTags: string[]
  onTagsChange: (tagIds: string[]) => void
  loading?: boolean
}

export function TagFilter({ tags, selectedTags, onTagsChange, loading = false }: TagFilterProps) {
  const [tagSearchQuery, setTagSearchQuery] = useState("")
  const [isTagFilterOpen, setIsTagFilterOpen] = useState(false)

  const filteredTags = useMemo(() => {
    if (!tagSearchQuery.trim()) {
      return tags.slice(0, 100)
    }
    return tags
      .filter((tag) => tag.name.toLowerCase().includes(tagSearchQuery.toLowerCase()))
      .slice(0, 100)
  }, [tags, tagSearchQuery])

  const handleTagToggle = (tagId: string) => {
    if (selectedTags.includes(tagId)) {
      onTagsChange(selectedTags.filter((id) => id !== tagId))
    } else {
      onTagsChange([...selectedTags, tagId])
    }
  }

  const getSelectedTagNames = () => {
    return selectedTags.map((tagId) => tags.find((tag) => tag.id === tagId)?.name).filter(Boolean)
  }

  return (
    <Popover open={isTagFilterOpen} onOpenChange={setIsTagFilterOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          className={cn(
            "text-muted-foreground",
            selectedTags.length > 0 && "bg-blue-50 text-blue-700 hover:bg-blue-100"
          )}
        >
          Filter
          {selectedTags.length > 0 && (
            <span className="ml-1 inline-flex h-4 w-4 items-center justify-center rounded-full bg-blue-600 text-xs text-white">
              {selectedTags.length}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-96 p-0" align="end">
        <div className="p-4">
          <div className="mb-4 flex items-center gap-2">
            <h3 className="text-lg font-semibold">Filter</h3>
          </div>

          <div className="relative mb-4">
            <div className="absolute inset-y-0 left-0 flex items-center pl-3">
              <svg
                className="h-4 w-4 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
            <input
              type="text"
              placeholder="Search tags"
              value={tagSearchQuery}
              onChange={(e) => setTagSearchQuery(e.target.value)}
              className="w-full rounded-lg border border-gray-300 bg-gray-50 py-2 pl-10 pr-3 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>

          <div className="flex flex-wrap gap-2">
            {loading ? (
              <div className="flex w-full items-center justify-center py-4">
                <div className="text-sm text-gray-500">Loading tags...</div>
              </div>
            ) : (
              <>
                {filteredTags.map((tag) => {
                  const isSelected = selectedTags.includes(tag.id)
                  return (
                    <Tag
                      key={tag.id}
                      selected={isSelected}
                      className="cursor-pointer transition-colors hover:opacity-80"
                      onClick={() => handleTagToggle(tag.id)}
                    >
                      {tag.name}
                    </Tag>
                  )
                })}
                {filteredTags.length === 0 && tagSearchQuery && (
                  <div className="text-sm text-gray-500">No tags found for "{tagSearchQuery}"</div>
                )}
                {filteredTags.length === 0 && !tagSearchQuery && !loading && (
                  <div className="text-sm text-gray-500">No tags available</div>
                )}
              </>
            )}
          </div>

          {selectedTags.length > 0 && (
            <div className="mt-4 border-t pt-4">
              <div className="mb-2 text-xs text-gray-600">
                Selected: {getSelectedTagNames().join(", ")}
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onTagsChange([])}
                className="text-muted-foreground"
              >
                Clear all
              </Button>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}
